/**
 * @copyright Microsoft 2020
 */
import { getWindow, getDocument, isNullOrUndefined, attachEvent } from "@microsoft/applicationinsights-core-js";
import { isRightClick, isLeftClick, isKeyboardEnter, isKeyboardSpace, isMiddleClick, isElementDnt } from "../common/Utils";
import { ActionType } from "../Enums";
var clickCaptureInputTypes = { BUTTON: true, CHECKBOX: true, RADIO: true, RESET: true, SUBMIT: true };
var AutoCaptureHandler = /** @class */ (function () {
    /**
     * @param analyticsPlugin - WebAnalytics plugin
     * @param traceLogger - Trace logger to log to console.
     */
    function AutoCaptureHandler(_analyticsPlugin, _config, _pageAction, _traceLogger) {
        this._analyticsPlugin = _analyticsPlugin;
        this._config = _config;
        this._pageAction = _pageAction;
        this._traceLogger = _traceLogger;
    }
    // handle automatic event firing on user click
    AutoCaptureHandler.prototype.click = function () {
        var _this = this;
        var win = getWindow();
        var doc = getDocument();
        if (win) {
            // IE9 onwards addEventListener is available, 'click' event captures mouse click. mousedown works on other browsers
            var event_1 = (navigator.appVersion.indexOf("MSIE") !== -1) ? "click" : "mousedown";
            attachEvent(win, event_1, function (evt) { _this._processClick(evt); });
            attachEvent(win, "keyup", function (evt) { _this._processClick(evt); });
        }
        else if (doc) {
            // IE8 and below doesn't have addEventListener so it will use attachEvent
            // attaching to window does not work in IE8
            attachEvent(doc, "click", function (evt) { _this._processClick(evt); });
            attachEvent(doc, "keyup", function (evt) { _this._processClick(evt); });
        }
    };
    /**
     * API to create and send a populated PageAction event
     * @param element - DOM element
     * @param overrideValues - PageAction overrides
     * @param customProperties - Custom properties(Part C)
     * @param isRightClick - Flag for mouse right clicks
     */
    AutoCaptureHandler.prototype.capturePageAction = function (element, overrideValues, customProperties, isRightClick) {
        var donotTrackTag = this._config.dataTags.customDataPrefix + this._config.dataTags.dntDataTag;
        if (!isElementDnt(element, donotTrackTag)) {
            this._pageAction.capturePageAction(element, overrideValues, customProperties, isRightClick);
        }
    };
    // Process click event
    AutoCaptureHandler.prototype._processClick = function (clickEvent) {
        var clickCaptureElements = { A: true, BUTTON: true, AREA: true, INPUT: true };
        var win = getWindow();
        if (isNullOrUndefined(clickEvent) && win) {
            clickEvent = win.event; // IE 8 does not pass the event
        }
        if (clickEvent) {
            var element = clickEvent.srcElement || clickEvent.target;
            // populate overrideValues
            var overrideValues = {
                clickCoordinateX: clickEvent.pageX,
                clickCoordinateY: clickEvent.pageY
            };
            var isRightClickObj = isRightClick(clickEvent);
            if (isRightClickObj) {
                overrideValues.actionType = ActionType.CLICKRIGHT;
            }
            else if (isLeftClick(clickEvent)) {
                overrideValues.actionType = ActionType.CLICKLEFT;
            }
            else if (isKeyboardEnter(clickEvent)) {
                overrideValues.actionType = ActionType.KEYBOARDENTER;
            }
            else if (isKeyboardSpace(clickEvent)) {
                overrideValues.actionType = ActionType.KEYBOARDSPACE;
            }
            else if (isMiddleClick(clickEvent)) {
                overrideValues.actionType = ActionType.CLICKMIDDLE;
            }
            else {
                return;
            }
            while (element && element.tagName) {
                // control property will be available for <label> elements with 'for' attribute, only use it when is a
                // valid JSLL capture element to avoid infinite loops
                if (element.control && clickCaptureElements[element.control.tagName.toUpperCase()]) {
                    element = element.control;
                }
                var tagNameUpperCased = element.tagName.toUpperCase();
                if (!clickCaptureElements[tagNameUpperCased]) {
                    element = element.parentElement || element.parentNode;
                    continue;
                }
                else {
                    // Check allowed INPUT types
                    var sendEvent = tagNameUpperCased === "INPUT" ? clickCaptureInputTypes[element.type.toUpperCase()] : true;
                    if (sendEvent) {
                        this.capturePageAction(element, overrideValues, {}, isRightClickObj);
                    }
                    break;
                }
            }
        }
    };
    return AutoCaptureHandler;
}());
export { AutoCaptureHandler };
//# sourceMappingURL=AutoCaptureHandler.js.map